import { FilterColors, FilterFonts } from "../../../state/filter";
import { PageIndex, PageSizeAtom } from "../../../state/page";
import { useContext, useEffect, useRef, useState } from "react";

import AudioControls from "./AudioControls";
import { IsInFlexBoxContext } from "../../displayElements/Cell";
import { type Modifier, text } from "@quickpoint.me/shared";
import StyledAudioPlayer from "./StyledAudioPlayer";
import { useRecoilValue } from "recoil";

/*
 * Read the blog post here:
 * https://letsbuildui.dev/articles/building-an-audio-player-with-react-hooks
 */
const AudioPlayer = ({
	title,
	audioSrc,
	modifier,
	tapped,
	elementIndex,
	pageIndex,
	isInAuthor,
	isEmbedded,
}: {
	title: string;
	audioSrc: string;
	modifier?: Modifier;
	tapped: boolean;
	elementIndex: number;
	pageIndex: number;
	isInAuthor: boolean;
	isEmbedded: boolean;
}) => {
	// State
	const [trackProgress, setTrackProgress] = useState(0);
	const currentPage = useRecoilValue(PageIndex);
	const initState: boolean =
		(modifier?.["mute:"] === "no" && !isInAuthor && !isEmbedded) || false;
	const [isPlaying, setIsPlaying] = useState(initState);

	// Refs
	const audioRef = useRef(new Audio(audioSrc));
	const intervalRef = useRef<NodeJS.Timeout>();

	const fonts = useRecoilValue(FilterFonts);
	const pageSize = useRecoilValue(PageSizeAtom);
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const filterColors = useRecoilValue(FilterColors);

	// Destructure for conciseness
	const { duration } = audioRef.current;

	const currentPercentage = duration
		? `${(trackProgress / duration) * 100}%`
		: "0%";
	// played portion in ink, the rest a light ink wash — no palette drama
	const ink = filterColors.first ?? "#111";
	const trackStyling = `linear-gradient(to right, ${ink} ${currentPercentage}, color-mix(in srgb, ${ink} 22%, transparent) ${currentPercentage})`;

	const startTimer = () => {
		// Clear any timers already running
		clearInterval(intervalRef.current);
		intervalRef.current = setInterval(() => {
			setTrackProgress(audioRef.current.currentTime);
		}, 1000);
	};

	const onScrub = (value: string) => {
		// Clear any timers already running
		clearInterval(intervalRef.current);
		audioRef.current.currentTime = Number.parseInt(value);
		setTrackProgress(audioRef.current.currentTime);
	};

	const onScrubEnd = () => {
		// If not already playing, start
		if (!isPlaying) {
			setIsPlaying(true);
		}
		startTimer();
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (isPlaying) {
			audioRef.current.play();
			startTimer();
		} else {
			audioRef.current.pause();
		}
	}, [isPlaying]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (currentPage !== pageIndex) {
			setIsPlaying(false);
			audioRef.current.pause();
			startTimer();
		}
		if (currentPage === pageIndex) {
			startTimer();
			setIsPlaying(initState);
		}
	}, [currentPage, pageIndex, initState]);

	useEffect(() => {
		// Pause and clean up on unmount
		return () => {
			audioRef.current.pause();
			clearInterval(intervalRef.current);
		};
	}, []);

	return (
		<StyledAudioPlayer
			className="audio"
			$baseFontSize={pageSize.baseFontSize}
			$baseFontSizeRaw={pageSize.baseFontSizeRaw}
			$elementIndex={elementIndex}
			$filterColors={filterColors}
			$fonts={fonts}
			$isInFlexBox={isInFlexBox}
			$modifier={modifier}
			$onePercentHeight={pageSize.onePercentHeight}
			$onePercentWidth={pageSize.onePercentWidth}
			$pageSize={pageSize}
			$isInPortrait={!pageSize.landscape}
			$tapped={tapped}
		>
			<div className="audio-player color-backdrop">
				<AudioControls isPlaying={isPlaying} onPlayPauseClick={setIsPlaying} />
				<div className="track-info">
					<div className="title">{title}</div>
					<input
						aria-label={text.reader.audioSeek}
						className="progress"
						max={duration && !Number.isNaN(duration) ? duration : 100}
						min="0"
						onChange={(e) => onScrub(e.target.value)}
						onKeyUp={onScrubEnd}
						onMouseUp={onScrubEnd}
						step="1"
						style={{ background: trackStyling }}
						type="range"
						value={trackProgress}
					/>
				</div>
			</div>
		</StyledAudioPlayer>
	);
};

export default AudioPlayer;
